import { type FormEvent, useEffect, useState } from "react";
import { readAdminSession } from "./lib/adminRepository";
import type { EventRole } from "./lib/eventAdminRepository";
import "./event-code-manager.css";

type BookingCode = {
  id: string;
  code: string;
  label: string;
  event_id: string | null;
  event_role_id: string | null;
  discount_type: "free" | "percent" | "fixed";
  discount_value: number;
  max_redemptions: number | null;
  redemption_count: number;
  valid_until: string | null;
  is_active: boolean;
};

const ENDPOINT = "https://cnejticdknokawqlslsj.supabase.co/functions/v1/booking-admin-codes";

export function EventCodeManager({ eventId, roles }: { eventId: string; roles: EventRole[] }) {
  const [codes, setCodes] = useState<BookingCode[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function loadCodes(targetEventId = eventId) {
    setBusy(true);
    setError(null);
    try {
      const response = await adminRequest(`${ENDPOINT}?event_id=${encodeURIComponent(targetEventId)}`);
      const payload = (await response.json()) as { codes?: BookingCode[]; error?: string };
      if (!response.ok) throw new Error(codeError(payload.error));
      setCodes((payload.codes ?? []).filter((code) => code.event_id === targetEventId));
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Einladungscodes konnten nicht geladen werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: event change is the intended reload trigger.
  useEffect(() => {
    void loadCodes(eventId);
  }, [eventId]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setBusy(true);
    setError(null);
    setMessage(null);
    const form = new FormData(formElement);
    try {
      const discountType = String(form.get("discountType") ?? "free");
      const rawValue = Number(form.get("discountValue") ?? 0);
      const rawValidUntil = String(form.get("validUntil") ?? "").trim();
      const validUntil = rawValidUntil ? new Date(rawValidUntil).toISOString() : null;
      const response = await adminRequest(ENDPOINT, {
        method: "POST",
        body: JSON.stringify({
          action: "save_code",
          eventId,
          eventRoleId: String(form.get("eventRoleId") ?? "") || null,
          code: String(form.get("code") ?? ""),
          label: String(form.get("label") ?? ""),
          discountType,
          discountValue: discountType === "fixed" ? Math.round(rawValue * 100) : rawValue,
          maxRedemptions: String(form.get("maxRedemptions") ?? "") || null,
          validUntil,
          isActive: true,
        }),
      });
      const payload = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(codeError(payload.error));
      formElement.reset();
      setMessage("Einladungscode ist aktiv.");
      setOpen(false);
      await loadCodes(eventId);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Einladungscode konnte nicht gespeichert werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function setActive(code: BookingCode, isActive: boolean) {
    setBusy(true);
    setError(null);
    try {
      const response = await adminRequest(ENDPOINT, {
        method: "POST",
        body: JSON.stringify({ action: "set_active", id: code.id, isActive }),
      });
      const payload = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(codeError(payload.error));
      setMessage(
        isActive ? "Einladungscode ist wieder aktiv." : "Einladungscode wurde deaktiviert.",
      );
      await loadCodes(eventId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Status konnte nicht geändert werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="event-code-manager" aria-label="Einladungscodes">
      <header>
        <div>
          <p className="workspace-kicker">Einladungen</p>
          <h3>Codes & Freiplätze</h3>
          <span>Für persönliche Einladungen, kostenfreie Plätze oder gezielte Preisnachlässe.</span>
        </div>
        <button type="button" onClick={() => setOpen((value) => !value)}>
          {open ? "Schließen" : "+ Code erstellen"}
        </button>
      </header>

      {message ? <p className="event-code-manager__notice">{message}</p> : null}
      {error ? (
        <p className="event-code-manager__error" role="alert">
          {error}
        </p>
      ) : null}

      {open ? (
        <form className="event-code-manager__form" onSubmit={save}>
          <label>
            Code
            <input name="code" placeholder="z. B. AUFSTELLUNG-MARIA" required maxLength={80} />
          </label>
          <label>
            Interne Bezeichnung
            <input name="label" placeholder="z. B. Freiplatz Maria" required maxLength={120} />
          </label>
          <label>
            Gilt für
            <select name="eventRoleId" defaultValue="">
              <option value="">Alle Teilnahmeformen dieser Veranstaltung</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Wirkung
            <select name="discountType" defaultValue="free">
              <option value="free">Kostenfreier Platz</option>
              <option value="percent">Prozent Rabatt</option>
              <option value="fixed">Fester Rabattbetrag</option>
            </select>
          </label>
          <label>
            Rabattwert
            <input name="discountValue" type="number" min="0" step="1" defaultValue="100" />
            <small>
              Bei Prozent: 0–100. Bei festem Rabatt: Betrag in Euro. Für Freiplatz wird der Wert
              ignoriert.
            </small>
          </label>
          <label>
            Max. Nutzungen <small>optional</small>
            <input name="maxRedemptions" type="number" min="1" step="1" />
          </label>
          <label>
            Gültig bis <small>optional</small>
            <input name="validUntil" type="datetime-local" />
          </label>
          <button type="submit" disabled={busy}>
            {busy ? "Wird gespeichert …" : "Code aktivieren"}
          </button>
        </form>
      ) : null}

      <div className="event-code-manager__list">
        {codes.map((code) => {
          const role = roles.find((item) => item.id === code.event_role_id);
          return (
            <article key={code.id} className={code.is_active ? "" : "is-inactive"}>
              <div>
                <strong>{code.code}</strong>
                <span>{code.label}</span>
                <small>
                  {role?.label ?? "Alle Teilnahmeformen"} · {discountLabel(code)}
                </small>
              </div>
              <div>
                <strong>
                  {code.redemption_count}
                  {code.max_redemptions ? ` / ${code.max_redemptions}` : ""}
                </strong>
                <small>Nutzungen</small>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void setActive(code, !code.is_active)}
                >
                  {code.is_active ? "Deaktivieren" : "Aktivieren"}
                </button>
              </div>
            </article>
          );
        })}
        {!busy && codes.length === 0 ? (
          <p>Noch keine Einladungscodes für diese Veranstaltung.</p>
        ) : null}
      </div>
    </section>
  );
}

async function adminRequest(url: string, init: RequestInit = {}) {
  const session = await readAdminSession();
  if (!session) throw new Error("Die Anmeldung ist abgelaufen. Bitte erneut anmelden.");
  return fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
}

function discountLabel(code: BookingCode) {
  if (code.discount_type === "free") return "kostenfrei";
  if (code.discount_type === "percent") return `${code.discount_value}% Rabatt`;
  return `${new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(code.discount_value / 100)} Rabatt`;
}

function codeError(value?: string) {
  const labels: Record<string, string> = {
    code_already_exists: "Dieser Code existiert bereits.",
    invalid_code: "Der Code darf nur Buchstaben, Zahlen, Bindestrich und Unterstrich enthalten.",
    invalid_discount_value: "Bitte den Rabattwert prüfen.",
    invalid_max_redemptions: "Die maximale Nutzung muss mindestens 1 sein.",
    event_role_not_found: "Die gewählte Teilnahmeform gehört nicht zu dieser Veranstaltung.",
    authentication_required: "Die Anmeldung ist abgelaufen. Bitte erneut anmelden.",
    admin_access_denied: "Für diese Funktion fehlt die Admin-Berechtigung.",
  };
  return labels[value ?? ""] ?? "Der Einladungscode konnte nicht verarbeitet werden.";
}
